import { useEffect, useRef, type ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';

import { Button } from './Button';
import { Icon } from './Icon';

/**
 * Dialogo modal.
 *
 * Usa o elemento nativo de dialogo do navegador. Ele ja entrega retencao de foco,
 * fechamento pela tecla de escape e a camada de fundo, tres comportamentos que uma
 * reimplementacao costuma acertar pela metade e que decidem se a tela e usavel por
 * teclado.
 *
 * `dismissible` falso faz o dialogo que so fecha pela acao dele: sem o botao de
 * fechar, sem escape e sem clique fora. Serve ao termo de uso com aceite
 * obrigatorio (P-USU-2), que bloqueia o uso ate o aceite.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  dismissible = true,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string | undefined;
  children: ReactNode;
  footer?: ReactNode | undefined;
  size?: 'sm' | 'md' | 'lg' | undefined;
  dismissible?: boolean | undefined;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      dialog.showModal();
    }
    if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  if (!open) return null;

  const widths = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
  } as const;

  return (
    <dialog
      ref={ref}
      onCancel={(event) => {
        event.preventDefault();
        if (dismissible) onClose();
      }}
      onClick={(event) => {
        // Clique fora do conteudo fecha. O alvo e o proprio dialogo apenas quando
        // o ponteiro caiu na area de fundo.
        if (dismissible && event.target === ref.current) onClose();
      }}
      className={cn(
        // A base do Tailwind zera a margem que centraliza o dialogo nativo.
        'm-auto w-[calc(100vw-2rem)] rounded-xl border border-line bg-surface p-0',
        'shadow-(--shadow-lg) backdrop:bg-ink-900/40',
        'open:animate-[fade-in_var(--duration-base)_ease-out]',
        widths[size],
      )}
    >
      <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
        <div>
          <h2 className="text-sm font-semibold text-ink-900">{title}</h2>
          {description ? (
            <p className="mt-0.5 text-xs text-ink-500">{description}</p>
          ) : null}
        </div>
        {dismissible ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label="Fechar"
            className="-mr-2 -mt-1"
          >
            <Icon name="close" size={16} />
          </Button>
        ) : null}
      </div>

      <div className="max-h-[70vh] overflow-y-auto px-5 py-4">{children}</div>

      {footer ? (
        <div className="flex justify-end gap-2 border-t border-line bg-surface-sunken px-5 py-3">
          {footer}
        </div>
      ) : null}
    </dialog>
  );
}

/**
 * Confirmacao de acao sensivel.
 *
 * Acao que muda estado ou retira acesso pede um passo a mais. O texto descreve a
 * consequencia, e nao apenas pergunta se a pessoa tem certeza.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirmar',
  loading,
  tone = 'primary',
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string | undefined;
  loading?: boolean | undefined;
  tone?: 'primary' | 'danger' | undefined;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button variant={tone} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-sm text-ink-600">{message}</p>
    </Modal>
  );
}
